/**
 * Sidebar conversation-list overflow hook.
 *
 * The `isConversationListOverflowing` state stays in the orchestrator (its
 * setter is threaded in) so hook order in the component body is unchanged: this
 * hook occupies the exact slot where the original callback+effect sat. Both
 * dependency arrays must stay as-is to preserve effect timing.
 */

import { useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

interface UseSidebarOverflowArgs {
  isMobile: boolean;
  conversationListRef: RefObject<HTMLElement | null>;
  sidebarRef: RefObject<HTMLElement | null>;
  setIsConversationListOverflowing: (value: boolean | ((prev: boolean) => boolean)) => void;
  displayedConversations: unknown;
  isLoading: boolean;
  isCollapsed: boolean;
  isChatsSectionOpen: boolean;
}

export function useSidebarOverflow({
  isMobile,
  conversationListRef,
  sidebarRef,
  setIsConversationListOverflowing,
  displayedConversations,
  isLoading,
  isCollapsed,
  isChatsSectionOpen,
}: UseSidebarOverflowArgs): void {
  const updateConversationListOverflow = useCallback(() => {
    if (isMobile) {
      setIsConversationListOverflowing(false);
      return;
    }
    const listEl = conversationListRef.current;
    const hasOverflow = Boolean(listEl && listEl.scrollHeight > listEl.clientHeight + 1);
    setIsConversationListOverflowing((prev) => (prev === hasOverflow ? prev : hasOverflow));
  }, [isMobile]);

  useEffect(() => {
    updateConversationListOverflow();
    const listEl = conversationListRef.current;
    if (!listEl) return;

    const handleResize = () => updateConversationListOverflow();
    window.addEventListener('resize', handleResize);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(handleResize)
      : null;
    resizeObserver?.observe(listEl);
    if (sidebarRef.current) {
      resizeObserver?.observe(sidebarRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [displayedConversations, isLoading, isCollapsed, isChatsSectionOpen, updateConversationListOverflow]);
}
